<template>
  <section class="doctor-comment-card">
    <div class="doctor-comment-card__title-row flex--space-between--center">
      <span class="doctor-comment-card__title">{{ $t('skincare.doctorComment.title') }}</span>
      <span
        v-if="comment && !isEditing"
        class="doctor-comment-card__edit pointer"
        @click="startEditing"
      >
        <span class="icon-pencil custom-icon"></span> {{ $t('dashboard.button.edit') }}
      </span>
    </div>

    <template v-if="!isEditing">
      <template v-if="comment">
        <p class="doctor-comment-card__text">{{ comment.text }}</p>
        <div class="doctor-comment-card__attribution">
          {{ $t('skincare.doctorComment.createdBy', { author: comment.authorName }) }} —
          {{ comment.creationDate | formatDate }}
          <span v-if="comment.edited">({{ $t('dashboard.label.edited') }})</span>
        </div>
      </template>
      <button
        v-else
        class="doctor-comment-card__add rounded-button-white"
        :class="{ 'rounded-button-white__alma': isAlmaTerritory }"
        @click="startEditing"
      >
        {{ $t('dashboard.label.addComment') }}
      </button>
    </template>

    <template v-else>
      <textarea
        v-model="draftText"
        class="doctor-comment-card__input rounded-input"
        :maxlength="MAX_LENGTH"
        :placeholder="$t('skincare.doctorComment.placeholder')"
        rows="3"
      />
      <div class="doctor-comment-card__counter">{{ draftText.length }} / {{ MAX_LENGTH }}</div>
      <div class="doctor-comment-card__actions flex--align-center">
        <button
          class="doctor-comment-card__cancel rounded-button-white"
          :class="{ 'rounded-button-white__alma': isAlmaTerritory }"
          :disabled="isSaving"
          @click="cancelEditing"
        >
          {{ $t('dashboard.action.cancel') }}
        </button>
        <button
          class="doctor-comment-card__save rounded-button-white"
          :class="{ 'rounded-button-white__alma': isAlmaTerritory }"
          :disabled="isSaving"
          @click="save"
        >
          {{ $t('dashboard.button.save') }}
        </button>
      </div>
    </template>
  </section>
</template>

<script>
import { getTreatmentComment, updateTreatmentComment } from '@/modules/dashboard/api';
import isAlmaTerritories from '@/modules/dashboard/components/mixins/isAlmaTerritories';

const MAX_LENGTH = 1000;

export default {
  name: 'DoctorCommentCard',
  mixins: [isAlmaTerritories],
  props: {
    treatmentId: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      MAX_LENGTH,
      comment: null,
      isEditing: false,
      isSaving: false,
      draftText: ''
    };
  },
  watch: {
    treatmentId: {
      immediate: true,
      handler() {
        this.loadComment();
      }
    }
  },
  methods: {
    async loadComment() {
      this.isEditing = false;
      this.draftText = '';
      try {
        const { data } = await getTreatmentComment(this.treatmentId);
        this.comment = data && data.text ? data : null;
      } catch (error) {
        this.comment = null;
      }
    },
    startEditing() {
      this.draftText = this.comment ? this.comment.text : '';
      this.isEditing = true;
    },
    cancelEditing() {
      this.isEditing = false;
      this.draftText = '';
    },
    async save() {
      this.isSaving = true;
      try {
        const { data } = await updateTreatmentComment(this.treatmentId, this.draftText.trim());
        this.comment = data && data.text ? data : null;
        this.isEditing = false;
      } catch (error) {
        // surface failures instead of swallowing them (a silent catch hid the original put() bug)
        // eslint-disable-next-line no-console
        console.error('Failed to save doctor comment', error);
      } finally {
        this.isSaving = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../../../../assets/css/common/inputs';
@import '../../../../assets/scss/helpers/colors';
@import '../../../../assets/scss/common/buttons';

.doctor-comment-card {
  display: block;
  margin-top: 20px;
  padding: 20px;
  background-color: $white;
  border: 1px solid $background;
  border-radius: 8px;

  &__title {
    font-size: 15px;
    font-weight: 500;
    color: $main-title-color;
  }

  &__edit {
    font-size: 13px;
    color: $light-blue;
  }

  &__add {
    margin-top: 12px;
  }

  &__text {
    margin: 12px 0 8px;
    font-size: 15px;
    color: $main-title-color;
    white-space: pre-wrap;
    word-wrap: break-word;
  }

  &__attribution {
    font-size: 12px;
    color: $date-grey-color;
  }

  &__input {
    width: 100%;
    min-height: 80px;
    margin-top: 12px;
    padding: 10px 15px;
    font-size: 15px;
    box-sizing: border-box;
    resize: vertical;
  }

  &__counter {
    margin-top: 6px;
    text-align: right;
    font-size: 12px;
    color: $grey-color;
  }

  &__actions {
    margin-top: 10px;

    button {
      margin-right: 10px;
    }
  }
}
</style>
